/**
 * [화면 편집] 위 막대의 [넣기] — 기본 요소를 그림과 이름으로 고른다(2026-10-30). 고르면 보이는 화면의 가운데에 놓인다.
 */
import type { ReactNode } from 'react';
import {
  CalendarClock,
  CalendarDays,
  Clapperboard,
  Hash,
  Heading,
  Image,
  ImagePlus,
  List,
  RectangleHorizontal,
  SeparatorHorizontal,
  Square,
  SquareCheck,
  SquareChevronDown,
  Table,
  TextCursorInput,
  Type,
} from 'lucide-react';
import { COMPONENTS, type ComponentId, type ComponentInfo } from './components.js';

const ICONS: Record<ComponentId, ReactNode> = {
  heading: <Heading className="h-5 w-5" />,
  text: <Type className="h-5 w-5" />,
  list: <List className="h-5 w-5" />,
  button: <RectangleHorizontal className="h-5 w-5" />,
  dday: <CalendarClock className="h-5 w-5" />,
  imageFrame: <Image className="h-5 w-5" />,
  image: <ImagePlus className="h-5 w-5" />,
  video: <Clapperboard className="h-5 w-5" />,
  box: <Square className="h-5 w-5" />,
  divider: <SeparatorHorizontal className="h-5 w-5" />,
  table: <Table className="h-5 w-5" />,
  input: <TextCursorInput className="h-5 w-5" />,
  number: <Hash className="h-5 w-5" />,
  date: <CalendarDays className="h-5 w-5" />,
  select: <SquareChevronDown className="h-5 w-5" />,
  checkbox: <SquareCheck className="h-5 w-5" />,
};

const GROUPS: ReadonlyArray<ComponentInfo['group']> = ['글', '그림·모양', '입력'];

export function InsertMenu({
  onPick,
  onClose,
  hidden = [],
}: {
  onPick: (id: ComponentId) => void;
  onClose: () => void;
  /** 이 편집기에서 쓸 수 없는 요소 — 예: 영상을 올릴 곳이 없을 때의 `video`. */
  hidden?: readonly ComponentId[];
}): ReactNode {
  return (
    <>
      <div className="fixed inset-0 z-[60]" onPointerDown={onClose} />
      <div
        role="menu"
        aria-label="넣기"
        className="absolute left-0 top-11 z-[61] w-[392px] rounded-xl bg-white p-3 shadow-[0_10px_32px_rgba(0,0,0,0.18),0_0_0_1px_rgba(0,0,0,0.06)]"
      >
        {GROUPS.map((group) => (
          <div key={group} className="mb-2 last:mb-0">
            <div className="mb-1.5 px-1 text-[12px] font-semibold text-slate-500">{group}</div>
            <div className="grid grid-cols-3 gap-1.5">
              {COMPONENTS.filter((item) => item.group === group && !hidden.includes(item.id)).map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  title={item.hint}
                  onClick={() => onPick(item.id)}
                  className="flex h-[58px] flex-col items-center justify-center gap-1 rounded-lg border border-slate-200 text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                >
                  {ICONS[item.id]}
                  <span className="text-[12.5px] font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
